import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  doc,
  setDoc,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import type { MedicalPlanData } from '@/types'

export const useMedicalPlanStore = defineStore('medicalPlan', () => {
  const medicalPlan = ref<MedicalPlanData | null>(null)
  const loading = ref(false)

  let unsubFn: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  function subscribeToMedicalPlan(incidentId: string, opId: string) {
    if (incidentId === _incidentId && opId === _opId && unsubFn) return
    if (unsubFn) unsubFn()
    _incidentId = incidentId
    _opId = opId
    loading.value = true
    unsubFn = onSnapshot(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'medical_plan', 'data'),
      (snap) => {
        medicalPlan.value = snap.exists() ? (snap.data() as MedicalPlanData) : null
        loading.value = false
      },
    )
  }

  async function saveMedicalPlan(incidentId: string, opId: string, data: MedicalPlanData) {
    const syncStore = useSyncStore()
    syncStore.pendingWrites++
    try {
      await setDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'medical_plan', 'data'),
        data,
      )
    } finally {
      syncStore.pendingWrites--
    }
  }

  function teardown() {
    if (unsubFn) { unsubFn(); unsubFn = null }
    _incidentId = ''
    _opId = ''
    medicalPlan.value = null
    loading.value = false
  }

  return { medicalPlan, loading, subscribeToMedicalPlan, saveMedicalPlan, teardown }
})
